import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  PLATFORM_ID,
  inject,
  DestroyRef,
  afterNextRender,
} from '@angular/core';
import { isPlatformBrowser, NgComponentOutlet } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { ContentContainer } from '../../../../core/content-container/content-container';
import { ScrollTo } from '../../../../shared/scroll-to/directives/scroll-to';
import { CosmicCanvas } from '../cosmic-canvas/cosmic-canvas';
import { CosmicHologram } from '../cosmic-hologram/cosmic-hologram';
import gsap from 'gsap';
import { map } from 'rxjs';
import { deferNotifications } from '../../../../core/rxjs/defer-notifications';

const DESKTOP_MEDIA_QUERY = '(min-width: 80rem)';

@Component({
  selector: 'app-banner',
  standalone: true,
  imports: [ContentContainer, ScrollTo, CosmicCanvas, CosmicHologram],
  templateUrl: './banner.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Banner {
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);

  readonly isDesktop = toSignal(
    this.breakpointObserver.observe(DESKTOP_MEDIA_QUERY).pipe(
      map(({ matches }) => this.isBrowser && matches),
      deferNotifications(),
    ),
    { initialValue: false },
  );

  constructor() {
    afterNextRender(() => {
      if (!this.isBrowser) return;

      const targets = this.getHeroAnimationTargets();

      if (targets.length > 0) {
        try {
          gsap.from(targets, {
            opacity: 0,
            y: 30,
            duration: 0.9,
            stagger: 0.15,
            ease: 'power3.out',
          });
        } catch {
          // GSAP can throw while reading computed styles (e.g. when a target
          // is detached mid hydration). The hero must stay usable, so this
          // entrance effect is best-effort and never surfaces as an error.
        }
      }
    });

    this.destroyRef.onDestroy(() => {
      if (this.isBrowser) {
        const targets = this.getHeroAnimationTargets();
        if (targets.length > 0) {
          try {
            gsap.killTweensOf(targets);
          } catch {
            // Cleanup is best-effort; never let it break teardown.
          }
        }
      }
    });
  }

  private getHeroAnimationTargets(): HTMLElement[] {
    const bannerElement = this.hostElement?.nativeElement as
      | HTMLElement
      | undefined;

    if (!bannerElement) return [];

    return ['title', 'description', 'cta']
      .map((target) =>
        bannerElement.querySelector<HTMLElement>(
          `[data-hero-entrance-target="${target}"]`,
        ),
      )
      .filter(
        (target): target is HTMLElement =>
          target !== null && target.isConnected,
      );
  }
}
